import { RautReportIndication } from 'features/indicationTriage'
import isNil from 'lodash/isNil'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { SavedIndicationGraph } from 'pages/reporting/shared/types'
import {
  GeckoInspection,
  GeckoMeasurement,
  GeckoSource,
  GeckoVisualInspection,
} from 'types'

export type ReportSection =
  | 'cover'
  | 'summary'
  | 'calibration'
  | 'indications'
  | 'clientImages'
  | 'assetDrawing'
  | 'weldScanIndications'
  | 'review'
  | 'generate'

interface SectionStatus {
  isComplete: boolean
  completedFields: number
  totalFields: number
}

type NestedKeys = string | string[]

type NestedRecord = {
  [key: string]: NestedRecord | unknown
}

/**
 * Required fields for different sections of the report
 */
const requiredCoverPageFields: NestedKeys[] = [
  'customerName',
  'asset',
  'location',
  'geckoJobId',
  'startDate',
  'endDate',
]

const requiredSummaryFields: NestedKeys[] = [
  'material',
  'surfaceTemperature',
  'temperatureUnit',
  'scopesByScanType',
  'inspectionSummary',
  'inspectionScope',
]

const requiredSharedCalibrationFields: NestedKeys[] = [
  'inspectionSurface',
  'surfaceCondition',
  'procedure',
  'scanMethods',
  'testingUnit',
  'model',
  'instrumentSN',
  'scannerType',
  'scannerSN',
  ['xMotor', 'function'],
  ['yMotor', 'function'],
]

const requiredTechniqueFields = [
  'techniqueName',
  'probeType',
  'probePartNumber',
  'probeSN',
  'wedgeType',
  'waveMode',
  'frequency',
  'elementQuantity',
  'pitch',
  'angle',
  'scanningGain',
  'velocity',
]

// Per-technique calibration required fields (with full paths)
const requiredTechniqueCalibrationFields: NestedKeys[] = [
  ['calibration', 'calBlockType'],
  ['calibration', 'calBlockSN'],
  ['calibration', 'reflectorType'],
]

// 0° Calibration fields
const required0DegCalibrationFields: NestedKeys[] = [
  ['calibration', 'thinStepDistance'],
  ['calibration', 'thickStepDistance'],
  ['calibration', 'verificationStepDistance'],
]

// Angle beam calibration fields
const requiredAngleBeamFields: NestedKeys[] = ['name', 'distance', 'amplitude']

const REQUIRED_DRAWING_TYPES = [
  'heatmap',
  'no-heatmap',
  'heatmap-with-indications',
] as const

interface CompletionStatusInput extends RautFormDataState {
  indicationImages?: {
    totalIndications: number
    capturedScreenshots: number
  }
  sortedIndications?: RautReportIndication[]
  savedIndicationGraphs?: Record<string, SavedIndicationGraph>
}

/**
 * Checks if a nested value exists and is not empty
 * Updated to handle GeckoMeasurement objects
 *
 * @param obj - Object to check
 * @param path - Path to the value
 * @returns boolean indicating if value exists and is not empty
 */
const checkNestedValue = (obj: NestedRecord, path: NestedKeys): boolean => {
  const fields = Array.isArray(path) ? path : [path]
  let current: NestedRecord | unknown = obj

  for (const field of fields) {
    if (
      !current ||
      typeof current !== 'object' ||
      !(field in current) ||
      current[field as keyof typeof current] === undefined ||
      current[field as keyof typeof current] === null ||
      current[field as keyof typeof current] === ''
    ) {
      return false
    }
    current = current[field as keyof typeof current]
  }

  // Special handling for GeckoMeasurement objects
  if (current instanceof GeckoMeasurement) {
    // A GeckoMeasurement is valid if it has a non-null, non-undefined num property
    return current.num !== undefined && current.num !== null
  }

  return true
}

/**
 * Calculates completion status for a section
 *
 * @param fields - Required fields for the section
 * @param data - Current form data
 * @returns Status object with completion details
 */
const calculateSectionStatus = (
  fields: NestedKeys[],
  data: NestedRecord
): SectionStatus => {
  const isAmbient =
    (data as { temperatureUnit?: string }).temperatureUnit === 'Ambient'

  // Exclude surfaceTemperature from required fields when Ambient
  const effectiveFields = isAmbient
    ? fields.filter((field) => field !== 'surfaceTemperature')
    : fields

  const completedFields = effectiveFields.filter((field) =>
    checkNestedValue(data, field)
  ).length

  return {
    isComplete: completedFields === effectiveFields.length,
    completedFields,
    totalFields: effectiveFields.length,
  }
}

/**
 * Gets the completion status for the calibration section
 */
const getCalibrationStatus = (calibrationData: NestedRecord): SectionStatus => {
  if (!calibrationData || !calibrationData.techniques) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields:
        requiredSharedCalibrationFields.length +
        requiredTechniqueFields.length +
        requiredTechniqueCalibrationFields.length,
    }
  }

  const completedSharedFields = requiredSharedCalibrationFields.filter(
    (field) => checkNestedValue(calibrationData, field)
  ).length

  const techniques = calibrationData.techniques as unknown[]
  if (!Array.isArray(techniques) || techniques.length === 0) {
    return {
      isComplete: false,
      completedFields: completedSharedFields,
      totalFields:
        requiredSharedCalibrationFields.length +
        requiredTechniqueFields.length +
        requiredTechniqueCalibrationFields.length,
    }
  }

  let completedTechniqueFields = 0
  let totalTechniqueFields = 0

  techniques.forEach((technique) => {
    const techniqueRecord = technique as NestedRecord
    const techniqueName = techniqueRecord.techniqueName as string | undefined

    requiredTechniqueFields.forEach((field) => {
      totalTechniqueFields++
      if (checkNestedValue(techniqueRecord, field)) {
        completedTechniqueFields++
      }
    })

    requiredTechniqueCalibrationFields.forEach((field) => {
      totalTechniqueFields++
      if (checkNestedValue(techniqueRecord, field)) {
        completedTechniqueFields++
      }
    })

    if (techniqueName && techniqueName.includes('Rapid AUT')) {
      required0DegCalibrationFields.forEach((field) => {
        totalTechniqueFields++
        if (checkNestedValue(techniqueRecord, field)) {
          completedTechniqueFields++
        }
      })
    }
  })

  const hasPAUT = techniques.some((t) => {
    const techniqueRecord = t as NestedRecord
    const techniqueName = techniqueRecord.techniqueName as string | undefined
    return techniqueName && techniqueName.includes('PAUT')
  })

  let completedAngleBeamFields = 0
  let totalAngleBeamFields = 0

  if (hasPAUT) {
    const angleBeamCalibration = calibrationData.angleBeamCalibration as
      | unknown[]
      | undefined
    if (Array.isArray(angleBeamCalibration)) {
      angleBeamCalibration.forEach((entry) => {
        const entryRecord = entry as NestedRecord
        requiredAngleBeamFields.forEach((field) => {
          totalAngleBeamFields++
          if (checkNestedValue(entryRecord, field)) {
            completedAngleBeamFields++
          }
        })
      })
    }
  }

  const totalFields =
    requiredSharedCalibrationFields.length +
    totalTechniqueFields +
    totalAngleBeamFields
  const completedFields =
    completedSharedFields + completedTechniqueFields + completedAngleBeamFields

  return {
    isComplete: completedFields === totalFields && totalFields > 0,
    completedFields,
    totalFields,
  }
}

/**
 * Gets the completion status for indications section.
 * Completeness is determined by the number of indications that have rendered visualizations,
 * which are auto-generated and stored directly on the indication entries.
 *
 * A value of -1 for capturedScreenshots indicates the data is still loading.
 */
const getIndicationsStatus = (data: CompletionStatusInput): SectionStatus => {
  if (!data.indicationImages) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields: 1,
    }
  }

  const {
    totalIndications,
    capturedScreenshots: indicationsWithVisualizationsCount,
  } = data.indicationImages

  // -1 indicates loading state - show 0 completed but don't mark as incomplete yet
  const isLoading = indicationsWithVisualizationsCount === -1
  const completedCount = isLoading ? 0 : indicationsWithVisualizationsCount

  return {
    // During loading, don't mark as complete or incomplete definitively
    isComplete:
      !isLoading && completedCount === totalIndications && totalIndications > 0,
    completedFields: completedCount,
    totalFields: totalIndications || 1,
  }
}

/**
 * Gets the completion status for client images section
 */
const getClientImagesStatus = (data: CompletionStatusInput): SectionStatus => {
  const images = data.clientProvidedImagesData?.images || []
  const hasAtLeastOneImage = images.length > 0

  return {
    isComplete: hasAtLeastOneImage,
    completedFields: images.length,
    totalFields: Math.max(1, images.length), // At least one image required
  }
}

/**
 * Gets the completion status for asset drawings section
 */
const getAssetDrawingsStatus = (
  data: CompletionStatusInput,
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
): SectionStatus => {
  const imageBundle = data.asset_drawings ?? {}
  const totalInspections = inspections.length
  const completedInspections = inspections.reduce((acc, inspection) => {
    const isComplete =
      inspection.data.inspection_type === GeckoSource.PhasedArray.id
        ? !!imageBundle?.[inspection.id]?.['welds-no-heatmap']
        : REQUIRED_DRAWING_TYPES.every(
            (type) => !!imageBundle?.[inspection.id]?.[type]
          )
    return acc + (isComplete ? 1 : 0)
  }, 0)

  return {
    isComplete: completedInspections === totalInspections,
    completedFields: completedInspections,
    totalFields: totalInspections,
  }
}

/**
 * Gets the completion status for signoff section
 */
const getSignoffStatus = (data: CompletionStatusInput): SectionStatus => {
  const signoff = data.signoff
  if (!signoff) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields: 2,
    }
  }

  let completedFields = 0
  if (signoff.supervisorSignoff) completedFields++
  if (signoff.reviewerSignoff) completedFields++

  return {
    isComplete: completedFields === 2,
    completedFields,
    totalFields: 2,
  }
}

const isFieldValid = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== ''
  }

  if (value instanceof GeckoMeasurement) {
    return !isNil(value.num)
  }

  if (typeof value === 'number') {
    return true
  }

  return !isNil(value)
}

const getWeldScanIndicationsStatus = (
  data: CompletionStatusInput,
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
): SectionStatus => {
  const hasPAUT = inspections.some(
    (inspection) =>
      inspection.data.inspection_type === GeckoSource.PhasedArray.id
  )

  if (!hasPAUT) {
    return {
      isComplete: true,
      completedFields: 0,
      totalFields: 0,
    }
  }

  if (!data.weldScanData || !data.weldScanData.scans) {
    return {
      isComplete: false,
      completedFields: 0,
      totalFields: 1,
    }
  }

  const { totalScans, scans } = data.weldScanData

  if (isNil(totalScans) || totalScans === 0) {
    return {
      isComplete: true,
      completedFields: 0,
      totalFields: 0,
    }
  }

  if (scans.length !== totalScans) {
    return {
      isComplete: false,
      completedFields: scans.length,
      totalFields: totalScans,
    }
  }

  let completedScansMetadata = 0
  let totalIndicationsExpected = 0

  scans.forEach((scan) => {
    const hasAllRequiredFields =
      isFieldValid(scan.scanLocation) &&
      isFieldValid(scan.scanLabel) &&
      isFieldValid(scan.scanStart) &&
      isFieldValid(scan.scanStop) &&
      isFieldValid(scan.centerlineNeg) &&
      isFieldValid(scan.centerlinePos) &&
      isFieldValid(scan.totalIndications)

    if (hasAllRequiredFields) {
      completedScansMetadata++
    }

    totalIndicationsExpected += scan.totalIndications ?? 0
  })

  const savedVisualizationsCount = Object.keys(
    data.savedWeldIndicationGraphs || {}
  ).length

  const completedVisualizations = Math.min(
    savedVisualizationsCount,
    totalIndicationsExpected
  )

  const metadataComplete = completedScansMetadata === totalScans
  const visualizationsComplete =
    completedVisualizations >= totalIndicationsExpected

  return {
    isComplete: metadataComplete && visualizationsComplete,
    completedFields: completedScansMetadata + completedVisualizations,
    totalFields: totalScans + totalIndicationsExpected,
  }
}

/**
 * Gets the completion status for all report sections
 *
 * @param formData - Current form data state
 * @param inspections - Array of all inspections in the report
 * @returns Record of completion status for each section
 */
export const getCompletionStatus = (
  formData: CompletionStatusInput,
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
): Record<string, SectionStatus> => {
  const coverPageCompletionData = {
    ...formData.coverPageData,
    ...formData.mainInfo,
  }
  return {
    cover: calculateSectionStatus(
      requiredCoverPageFields,
      coverPageCompletionData as unknown as NestedRecord
    ),
    summary: calculateSectionStatus(requiredSummaryFields, {
      ...formData.summaryPageData,
      inspectionSummary: formData.summaryPageData.inspectionSummary || {},
      ...formData.mainInfo,
    } as unknown as NestedRecord),
    calibration: getCalibrationStatus(
      formData.calibrationData as unknown as NestedRecord
    ),
    indications: getIndicationsStatus(formData),
    clientImages: getClientImagesStatus(formData),
    assetDrawing: getAssetDrawingsStatus(formData, inspections),
    weldScanIndications: getWeldScanIndicationsStatus(formData, inspections),
    review: getSignoffStatus(formData),
    generate: {
      isComplete: true, // Generate section is always considered complete
      completedFields: 1,
      totalFields: 1,
    },
  }
}
